"use client";
import { getInitialTouchedValues } from "@lib/utils/checkoutHelper";
import getDeliveryStepValidationSchema from "@lib/validators/checkout/deliveryStepValidationSchema";
import { Text } from "@ecco/ui/text";
import { Checkbox } from "@ecco/ui/checkbox";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { Form, Formik, useFormikContext } from "formik";
import type { FC, JSX } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import StepForm from "../StepForm";
import StepSubmitButton from "../StepSubmitButton";
import BillingAddressSubForm from "./BillingAddressSubForm";
import { DeliveryMethods } from "./delivery-methods/DeliveryMethods";
import { styles } from "./DeliveryStepFormStyles";
import AddressFormFields from "./AddressFormFields";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";
import { RichText } from "@components/cms/rich-text-renderer/RichText";
import { activeSiteConfig } from "@config/site/site-config";
import { BILLING_PREFIX, SHIPPING_PREFIX } from "@lib/constants/addresses";
import {
    DELIVERY_STEP_FORM_TEST_ID,
    DELIVERY_SUBMIT_BUTTON_TEST_ID,
    SAME_BILLING_ADDRESS_CHECKBOX,
    VAT_TEST_ID,
} from "@e2e/helpers/checkout/components/shipping-details/constants";
import { ShippingMethodTypes } from "@features/checkout/types";
import type { CountryCode } from "@config/site/types";
import { getDeliveryMethodKey } from "@lib/utils/findDeliveryMethods";
import { useDeliveryStepFormMicrocopy } from "./DeliveryStepForm.microcopy";
import type { CheckoutFormInitialValues, DeliveryStepFormProps, FormDetailsProps } from "./types";
import { DeliveryStepSummary } from "./DeliveryStepSummary";
import { getFormsConfig } from "./FormsConfig";
import BaseInputField from "@components/commerce/baseForms/BaseInputField";
import { fieldStateHelper } from "@lib/utils/form/fieldStateHelper";
import { useFormFieldsMicrocopy } from "./FormFields.microcopy";
import { useCheckoutErrorState } from "@features/checkout/utils/useCheckoutErrorState";
import { useLocale } from "next-intl";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { CheckoutProgressSubtypes } from "@lib/analytics/analyticsTypes";
import { useIsLoyaltyMember } from "@hooks/useIsLoyaltyMember";
import { RUM_ACTION_NAME } from "@lib/monitoring/rumActionNames";

const FormValidator = ({ shouldValidate }: { shouldValidate: boolean }) => {
    const { setFieldTouched } = useFormikContext<CheckoutFormInitialValues>();

    useEffect(() => {
        if (shouldValidate) {
            ["deliveryAddress", "billingAddress"].forEach((prefix) => {
                Object.keys(initialAddressObject).forEach((key) => {
                    setFieldTouched(`${prefix}.${key}`, true, true);
                });
            });
        }
    }, [shouldValidate, setFieldTouched]);

    return null;
};

const initialAddressObject = {
    firstName: null,
    lastName: null,
    streetName: null,
    additionalAddressInfo: null,
    postalCode: null,
    city: null,
    region: null,
    country: null,
    landmark: null,
    deliveryInstructions: null,
    district: null,
    company: null,
};

const initialObject = {
    deliveryAddress: initialAddressObject,
    billingAddress: initialAddressObject,
};

// Reports the live "order as company" checkbox value up to the parent so the
// auth-invitation block can be hidden while the address step is being edited.
const CompanyOrderWatcher: FC<{
    isCompany: boolean;
    onChange?: (isCompany: boolean) => void;
}> = ({ isCompany, onChange }) => {
    useEffect(() => {
        onChange?.(isCompany);
    }, [isCompany, onChange]);
    return null;
};

const FormDetails: FC<FormDetailsProps> = ({
    handleSubmit,
    initialValues,
    shippingMethods,
    cartId,
    shouldValidate = false,
    onCompanyOrderChange,
}): JSX.Element => {
    const locale = useLocale();
    const countryCode = activeSiteConfig.country;
    const { enableVatNumber } = getFormsConfig(countryCode);
    const {
        isLoyaltyMember,
        isKnown: isLoyaltyMembershipKnown,
        isLoading: isLoyaltyMemberLoading,
    } = useIsLoyaltyMember();
    const { headers: formFieldHeaders } = useFormFieldsMicrocopy();
    const { headerErrors, headerLabels } = useDeliveryStepFormMicrocopy();
    const [billingAddressCountry, setBillingAddressCountry] = useState<CountryCode>(
        initialValues.billingAddress.country || countryCode
    );
    const formRef = useRef<HTMLFormElement>(null);
    const reversedShippingMethods = shippingMethods && [...shippingMethods]?.reverse();
    const hasFormStartedEventFired = useRef(false);
    const [isSubmitPending, setIsSubmitPending] = useState(false);

    useEffect(() => {
        if (isLoyaltyMemberLoading || !isSubmitPending) return;

        setIsSubmitPending(false);
        formRef.current?.requestSubmit();
    }, [isLoyaltyMemberLoading, isSubmitPending]);

    const getMethodName = (methodKey: string): string => {
        const selectedMethod = shippingMethods?.find((m) => m.key === methodKey);
        return selectedMethod?.localizedName?.[locale] ?? selectedMethod?.name ?? methodKey;
    };

    // Fire event for default delivery method when step loads
    useEffect(() => {
        if (initialValues.deliveryMethodId && typeof window !== "undefined" && window.dataLayer) {
            // Check if default deliveryMethodSelected event already exists
            const hasDefaultDeliveryMethod = window.dataLayer.some(
                (event) =>
                    event.event === "checkout_progress" &&
                    event.step_name === CheckoutProgressSubtypes.deliveryMethodSelected &&
                    event.type === "default"
            );

            // Only fire default delivery method event if it hasn't been fired before
            if (!hasDefaultDeliveryMethod) {
                AnalyticsClient.checkoutProgress(
                    CheckoutProgressSubtypes.deliveryMethodSelected,
                    getMethodName(initialValues.deliveryMethodId),
                    "default"
                );
            }
        }
    }, [initialValues.deliveryMethodId, shippingMethods, locale]);

    const isStateFieldEnabled = useFeatureFlag("isStateFieldEnabled");
    const isDistrictFieldEnabled = useFeatureFlag("isDistrictFieldEnabled");

    const valSchema = useMemo(() => {
        return getDeliveryStepValidationSchema(
            headerErrors,
            countryCode,
            billingAddressCountry,
            locale,
            { isStateFieldEnabled, isDistrictFieldEnabled },
            isLoyaltyMember
        );
    }, [
        headerErrors,
        countryCode,
        billingAddressCountry,
        locale,
        isStateFieldEnabled,
        isDistrictFieldEnabled,
        isLoyaltyMember,
    ]);

    const isPickUpMethod = (deliveryMethodId: string) => {
        return (
            deliveryMethodId === getDeliveryMethodKey(ShippingMethodTypes.CLICK_AND_COLLECT) ||
            deliveryMethodId === getDeliveryMethodKey(ShippingMethodTypes.PARCEL_SHOP)
        );
    };

    const getInitialTouched = () => {
        return {
            ...getInitialTouchedValues(initialValues),
            [SHIPPING_PREFIX]: getInitialTouchedValues(initialValues[SHIPPING_PREFIX]),
            [BILLING_PREFIX]: getInitialTouchedValues(initialValues[BILLING_PREFIX]),
        };
    };

    return (
        <Formik<CheckoutFormInitialValues>
            enableReinitialize={true}
            initialValues={initialValues}
            initialErrors={initialObject}
            initialTouched={getInitialTouched()}
            validationSchema={valSchema}
            onSubmit={(values, actions) => {
                const isPickupDelivery = isPickUpMethod(values.deliveryMethodId);
                const isCompanyOrder =
                    values.hasSameBillingAddress && !isPickupDelivery
                        ? Boolean(values.deliveryAddress.isCompany)
                        : Boolean(values.billingAddress.isCompany);

                if (isLoyaltyMemberLoading || (isCompanyOrder && !isLoyaltyMembershipKnown)) {
                    if (isLoyaltyMemberLoading) setIsSubmitPending(true);
                    actions.setSubmitting(false);
                    return;
                }
                handleSubmit({
                    ...values,
                    // Club members can't submit a VAT/NIP even if a stale value lingers in state.
                    vatNumber: isLoyaltyMember ? "" : values.vatNumber,
                    deliveryAddress: {
                        ...values.deliveryAddress,
                        country: countryCode,
                    },
                });
                actions.setSubmitting(false);
                actions.setStatus("");
            }}
        >
            {({
                values,
                dirty,
                errors,
                touched,
                setFieldValue,
                isSubmitting,
                isValidating,
                isValid,
                status,
                setSubmitting,
            }) => {
                const { getFieldStateProps, getIconProps } = fieldStateHelper({
                    errors,
                    touched,
                });
                const isPickupDelivery = isPickUpMethod(values.deliveryMethodId);

                // Track when form becomes dirty (fires once)
                if (!isPickupDelivery && dirty && !hasFormStartedEventFired.current) {
                    AnalyticsClient.checkoutProgress(
                        CheckoutProgressSubtypes.addShippingInfoFormStarted
                    );
                    hasFormStartedEventFired.current = true;
                }

                const isBillingCompanyOrder =
                    values.hasSameBillingAddress && !isPickupDelivery
                        ? Boolean(values.deliveryAddress.isCompany)
                        : Boolean(values.billingAddress.isCompany);

                const renderVatField = () =>
                    enableVatNumber &&
                    !isLoyaltyMember &&
                    isBillingCompanyOrder && (
                        <Box mt={4} mb={6}>
                            <BaseInputField
                                required={true}
                                {...getFieldStateProps("vatNumber")}
                                {...getIconProps("vatNumber")}
                                id={`vatNumber`}
                                testId={VAT_TEST_ID}
                                header={formFieldHeaders.vatNumber}
                            />
                        </Box>
                    );

                return (
                    <Form noValidate={true} data-testid={DELIVERY_STEP_FORM_TEST_ID} ref={formRef}>
                        <FormValidator shouldValidate={shouldValidate} />
                        <CompanyOrderWatcher
                            isCompany={isBillingCompanyOrder}
                            onChange={onCompanyOrderChange}
                        />
                        <Flex direction="column">
                            <DeliveryMethods
                                shippingMethods={reversedShippingMethods}
                                deliveryMethodId={values.deliveryMethodId}
                                formId={"deliveryMethodId"}
                                error={errors?.deliveryMethodId}
                                isInvalid={!!errors.deliveryMethodId && touched.deliveryMethodId}
                            />
                            {!isPickupDelivery && (
                                <Box sx={styles.fieldsWrapper}>
                                    <Text fontWeight={"semibold"}>
                                        {headerLabels.shippingAddress}
                                    </Text>
                                    <AddressFormFields
                                        prefix={SHIPPING_PREFIX}
                                        country={countryCode}
                                        isBillingForm={false}
                                        locale={locale}
                                        deliveryMethodId={values.deliveryMethodId}
                                        cartId={cartId}
                                    />
                                    <Box mt={4} mb={6}>
                                        <Checkbox
                                            data-testid={SAME_BILLING_ADDRESS_CHECKBOX}
                                            id="hasSameBillingAddress"
                                            name="hasSameBillingAddress"
                                            alignItems={"flex-start"}
                                            isChecked={values.hasSameBillingAddress}
                                            defaultChecked={values.hasSameBillingAddress}
                                            onChange={() => {
                                                setFieldValue(
                                                    "hasSameBillingAddress",
                                                    !values.hasSameBillingAddress
                                                );
                                            }}
                                        >
                                            {headerLabels.addressesMatch}
                                        </Checkbox>
                                    </Box>

                                    {
                                        // Render the VAT field if hasSameBillingAddress is checked. In that case we don't show the billing address form.
                                        values.hasSameBillingAddress && renderVatField()
                                    }
                                </Box>
                            )}
                            {(!values.hasSameBillingAddress || isPickupDelivery) && (
                                <BillingAddressSubForm
                                    country={billingAddressCountry}
                                    countryChanged={(country) => {
                                        setBillingAddressCountry(country);
                                        setFieldValue("billingAddress.region", "");
                                    }}
                                    locale={locale}
                                    isPickUpMethod={isPickupDelivery}
                                >
                                    {renderVatField()}
                                </BillingAddressSubForm>
                            )}
                            <Flex alignItems="start">
                                <StepSubmitButton
                                    testId={DELIVERY_SUBMIT_BUTTON_TEST_ID}
                                    headerText={headerLabels.continueToPayment}
                                    type="submit"
                                    data-dd-action-name={RUM_ACTION_NAME.CHECKOUT_DELIVERY_SUBMIT}
                                    isLoading={isLoyaltyMemberLoading || isSubmitPending}
                                    disabled={
                                        isLoyaltyMemberLoading ||
                                        isSubmitPending ||
                                        (isBillingCompanyOrder && !isLoyaltyMembershipKnown)
                                    }
                                    onClick={(event) => {
                                        const scrollToInvalid = () => {
                                            formRef?.current
                                                ?.querySelector("[data-invalid]")
                                                ?.scrollIntoView({
                                                    behavior: "smooth",
                                                    block: "center",
                                                });
                                        };

                                        // Because the async postalCode validation uses setStatus to set postalCodeError it will not stop a submit.
                                        // We need to check for that specific error here and prevent the submit if it exists.
                                        // Skip this check for pickup methods — a stale postalCodeError from a previous
                                        // home-delivery selection would block the submit. Parcel shop postal code
                                        // validation is handled separately in the submit handler (CheckoutClient).
                                        const hasPostalCodeError =
                                            Boolean(status?.postalCodeError) && !isPickupDelivery;
                                        if (hasPostalCodeError) {
                                            // set submitting state so formik will mark all fields as touched. If not errors might be hidden.
                                            setSubmitting(true);
                                            event.preventDefault();
                                            event.stopPropagation();
                                            scrollToInvalid();
                                            setSubmitting(false);
                                            return;
                                        }

                                        // scroll to first invalid input if the form is not valid on click.
                                        const shouldScrollToInvalid =
                                            !isValid && !isSubmitting && !isValidating;

                                        if (shouldScrollToInvalid) {
                                            scrollToInvalid();
                                        }
                                    }}
                                />
                            </Flex>
                        </Flex>
                    </Form>
                );
            }}
        </Formik>
    );
};

export const DeliveryStepForm = ({
    onSubmit,
    title,
    initialValues,
    shippingMethods,
    cart,
    shippingDetails,
    stepId,
    handleExpanded,
    isExpandedId,
    children,
    onCompanyOrderChange,
}: DeliveryStepFormProps) => {
    const { errors } = useCheckoutErrorState();
    const errorMessage = errors[stepId];

    return (
        <StepForm
            title={title}
            stepId={stepId}
            errorMessage={errorMessage}
            handleExpanded={handleExpanded}
            isExpandedId={isExpandedId}
            values={initialValues}
            summary={<DeliveryStepSummary values={initialValues} />}
        >
            {activeSiteConfig.checkout.hasShippingDetails && (
                <Box data-testid="shippingDetails-button">
                    <RichText data={shippingDetails} />
                </Box>
            )}
            <FormDetails
                handleSubmit={onSubmit}
                initialValues={initialValues}
                shippingMethods={shippingMethods}
                cartId={cart.id}
                shouldValidate={!!errorMessage}
                onCompanyOrderChange={onCompanyOrderChange}
            />
            {children}
        </StepForm>
    );
};

export default DeliveryStepForm;
